import Wizard, {
    WizardProps,
} from '@amzn/awsui-components-react/polaris/wizard';
import { useLazyRequest, useLocalStorage } from '@amzn/event-engine-js-utils';
import { state as breadcrumbsState } from 'atoms/breadcrumbs';
import {
    initialState as initialContentTypeState,
    state as contentTypeState,
} from 'atoms/content-type';
import { state as navigationContentState } from 'atoms/navigation-content';
import { state as toolsState } from 'atoms/tools';
import { AWSError } from 'aws-sdk/lib/error';
import { differenceInHours } from 'date-fns';
import { FormikProvider, setNestedObjectValues, useFormik } from 'formik';
import { StorageKeyID } from 'pages/EventDashboard/types';
import React, {
    useCallback,
    useEffect,
    useLayoutEffect,
    useState,
} from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { useSetRecoilState } from 'recoil';
import { EEEvents, ErrorCode, ErrorMessage, MessageTypes } from 'services';

import { routePaths, routes } from './../../constants';
import { FormSchema, FormSchemaType, Step } from './JoinEvent.validation';
import JoinEventSteps from './steps';
import { URLSearchParamKey } from './types';

const {
    StepOne: { EventAccessCode, isStepOneValid },
    StepTwo: { ReviewAndJoin, getReviewFormFieldName },
} = JoinEventSteps;

const JoinEventContainer = () => {
    const history = useHistory();
    const location = useLocation();
    const storage = useLocalStorage();
    const [validateOnChange, setValidateOnChange] = useState(false);
    const [activeStepIndex, setActiveStepIndex] = useState<number>(0);
    const [error, setError] = useState<AWSError>();
    const setContentType = useSetRecoilState(contentTypeState);
    const setBreadcrumbs = useSetRecoilState(breadcrumbsState);
    const setTools = useSetRecoilState(toolsState);
    const setNavigationContent = useSetRecoilState(navigationContentState);
    const urlAccessCode =
        new URLSearchParams(location.search).get(
            URLSearchParamKey.ACCESS_CODE
        ) || '';

    useLayoutEffect(() => {
        setBreadcrumbs([
            {
                text: 'Workshop Studio',
                href: routes.landingPage,
            },
            {
                text: 'Join event',
                href: routes.joinEvent,
            },
        ]);
        setTools({
            open: false,
            hide: true,
            content: <></>,
        });
        setNavigationContent(null);
        setContentType('wizard');

        return () => {
            setContentType(initialContentTypeState);
        };
    }, []);

    const [
        getEventCodeInfo,
        {
            isLoading: eventCodeInfoLoading,
            isError: eventCodeInfoError,
            data: eventCodeInfoData,
        },
    ] = useLazyRequest<typeof EEEvents.getParticipantEventCodeInfo, AWSError>(
        EEEvents.getParticipantEventCodeInfo,
        {}
    );

    const [
        joinEvent,
        {
            isLoading: joinEventLoading,
            isError: joinEventError,
            data: joinEventData,
        },
    ] = useLazyRequest<typeof EEEvents.joinEvent, AWSError>(
        EEEvents.joinEvent,
        {}
    );

    const onSubmit = async (values: FormSchemaType) => {
        await joinEvent(values[Step.ACCESS_CODE_STEP_ONE].accessCode);
    };

    const formik = useFormik({
        validationSchema: FormSchema,
        validateOnBlur: false,
        validateOnChange,
        initialValues: {
            [Step.ACCESS_CODE_STEP_ONE]: {
                accessCode: urlAccessCode,
            },
            [Step.REVIEW_STEP_TWO]: {
                eventName: '',
                startTime: '',
                description: '',
                duration: -1,
                level: '',
                termsAndConditions: false,
            },
        },
        onSubmit,
    });

    const onValidateForm = useCallback(async () => {
        const errors = await formik.validateForm();
        formik.setTouched(setNestedObjectValues(errors, true));
        setValidateOnChange(true);
    }, [formik]);

    const onAccessCodeClick = async (): Promise<boolean> => {
        const isValid = await isStepOneValid(formik);
        if (isValid) {
            setValidateOnChange(false);
            const accessCodeResponse = await getEventCodeInfo(
                formik.values[Step.ACCESS_CODE_STEP_ONE].accessCode
            );

            if (!accessCodeResponse) {
                return false;
            }

            if (!accessCodeResponse.eventIsActive) {
                const error =
                    accessCodeResponse.eventEndTime > new Date()
                        ? ErrorCode.EventNotReadyException
                        : ErrorCode.ExpiredEventException;
                setError({
                    name: error,
                    message: error,
                    code: error,
                    statusCode: 400,
                    time: new Date(),
                });

                return false;
            }

            return !!accessCodeResponse;
        } else {
            onValidateForm();
            return false;
        }
    };

    const onNavigate = useCallback(
        async ({
            detail: { requestedStepIndex },
        }: {
            detail: WizardProps.NavigateDetail;
        }) => {
            setError(undefined);
            if (activeStepIndex < requestedStepIndex) {
                const isValid = await onAccessCodeClick();

                if (isValid) {
                    setActiveStepIndex(requestedStepIndex);
                } else {
                    onValidateForm();
                }
            } else {
                setActiveStepIndex(requestedStepIndex);
                setValidateOnChange(false);
            }
        },
        [activeStepIndex, setActiveStepIndex, formik.values]
    );

    const onCancel = useCallback(() => {
        history.push(routes.landingPage);
    }, []);

    useEffect(() => {
        if (eventCodeInfoData) {
            formik.setFieldValue(
                getReviewFormFieldName('eventName'),
                eventCodeInfoData.eventTitle
            );
            formik.setFieldValue(
                getReviewFormFieldName('level'),
                eventCodeInfoData.eventLevel || '-'
            );
            formik.setFieldValue(
                getReviewFormFieldName('startTime'),
                eventCodeInfoData.eventStartTime
            );
            formik.setFieldValue(
                getReviewFormFieldName('duration'),
                differenceInHours(
                    eventCodeInfoData.eventEndTime,
                    eventCodeInfoData.eventStartTime
                )
            );
            formik.setFieldValue(
                getReviewFormFieldName('description'),
                eventCodeInfoData.eventDescription
            );
        }
    }, [eventCodeInfoData]);

    useEffect(() => {
        if (!joinEventData) {
            return;
        }

        storage.setItem(StorageKeyID.EVENT_DURATION_HIDE_TIME, '');
        history.push(`${routes.events}/${routePaths.dashboard}`, {
            participantId: joinEventData.participantId,
        });
    }, [joinEventData]);

    useEffect(() => {
        eventCodeInfoError && setError(eventCodeInfoError);
    }, [eventCodeInfoError]);

    useEffect(() => {
        joinEventError && setError(joinEventError);
    }, [joinEventError]);

    useEffect(() => {
        if (!urlAccessCode) {
            return;
        }

        onNavigate({
            detail: {
                requestedStepIndex: 1,
                reason: 'step',
            },
        });
        history.replace(location.pathname);
    }, []);

    return (
        <FormikProvider value={formik}>
            <Wizard
                i18nStrings={{
                    stepNumberLabel: (stepNumber) => `Step ${stepNumber}`,
                    collapsedStepsLabel: (stepNumber, stepsCount) =>
                        `Step ${stepNumber} of ${stepsCount}`,
                    cancelButton: 'Cancel',
                    previousButton: 'Previous',
                    nextButton: 'Next',
                    submitButton: 'Join event',
                    optional: 'optional',
                }}
                onCancel={onCancel}
                activeStepIndex={activeStepIndex}
                steps={[
                    {
                        title: 'Enter event access code',
                        content: (
                            <EventAccessCode
                                isValidating={eventCodeInfoLoading}
                            />
                        ),
                        errorText: error
                            ? ErrorMessage.getMessage(
                                  error,
                                  MessageTypes.getParticipantEventCodeInfo
                              )
                            : undefined,
                    },
                    {
                        title: 'Review and join',
                        content: (
                            <ReviewAndJoin isValidating={joinEventLoading} />
                        ),
                        errorText: error
                            ? ErrorMessage.getMessage(
                                  error,
                                  MessageTypes.joinEvent
                              )
                            : undefined,
                    },
                ]}
                onNavigate={onNavigate}
                onSubmit={formik.submitForm}
                isLoadingNextStep={eventCodeInfoLoading || joinEventLoading}
            />
        </FormikProvider>
    );
};

export default JoinEventContainer;
